import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  InputBase,
  Chip,
  Checkbox,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip
} from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import { StarRate, CalendarMonth, AttachMoney, Add, Remove, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../components/layouts/Layout';
import calendarService, { type CalendarEvent } from '../services/calendarService';
import { ReactComponent as TasksSvg } from '../assets/images/tasks.svg';
import { listMyTasks, createMyTask, completeMyTask } from '../services/localTasksService';
import api from '../services/api';
import { getSocket, onSttNoteGenerated } from '../services/realtimeService';
import { listNotes, type NoteListItem } from '../services/notesService';
import { listUsersPublic, type AppUser } from '../services/userService';
import projectService from '../services/projectService';
import NoteAiTasksDialog from '../components/notes/NoteAiTasksDialog';
import NotePreviewDrawer from '../components/notes/NotePreviewDrawer';
import { getParticipants, type MeetingParticipantDto } from '../services/sttApi';

// Zwiększ rozdzielczość zdjęcia profilowego Google (lh3.googleusercontent) do docelowego rozmiaru
function getHiResGoogleAvatar(rawUrl: string | undefined | null, targetSize = 256): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    let url = rawUrl;
    // Warianty: ...=s96-c, =s96, =w120-h120-..., ?sz=50
    url = url.replace(/=s(\d+)/, (_m, _d) => `=s${targetSize}`);
    url = url.replace(/([?&]sz=)(\d+)/, (_m, p1) => `${p1}${targetSize}`);
    url = url.replace(/=w(\d+)-h(\d+)/, () => `=w${targetSize}-h${targetSize}`);
    return url;
  } catch {
    return rawUrl;
  }
}

type RequestType = 'vacation' | 'advance';
type VacationKind = 'wypoczynkowy' | 'na_zadanie' | 'opieka_nad_dzieckiem' | 'inny';

const REQUEST_TYPE_OPTIONS: Array<{ id: RequestType; label: string; description: string }> = [
  { id: 'vacation', label: 'Wniosek urlopowy', description: 'Planowany urlop wypoczynkowy lub urlop na żądanie.' },
  { id: 'advance', label: 'Wniosek o zaliczkę', description: 'Środki na wydatki służbowe przed rozliczeniem.' }
];

const VACATION_KIND_OPTIONS: Array<{ id: VacationKind; label: string; description: string }> = [
  { id: 'wypoczynkowy', label: 'Urlop wypoczynkowy', description: 'Klasyczny urlop zgodnie z planem urlopowym (20 lub 26 dni w roku).' },
  { id: 'na_zadanie', label: 'Urlop na żądanie', description: 'Do wykorzystania w nagłych sytuacjach.' },
  { id: 'opieka_nad_dzieckiem', label: 'Opieka nad dzieckiem', description: '2 dni w roku (art. 188 KP) dla rodzica/opiekuna dziecka do 14 lat. Nie pomniejszają puli urlopu wypoczynkowego.' },
  { id: 'inny', label: 'Inny', description: 'np. okolicznościowy, opieka nad chorym dzieckiem, bezpłatny itp. Wpisz szczegóły w uwagach dla HR.' }
];

const VACATION_KIND_LABELS: Record<VacationKind, string> = {
  wypoczynkowy: 'Urlop wypoczynkowy',
  na_zadanie: 'Urlop na żądanie',
  opieka_nad_dzieckiem: 'Opieka nad dzieckiem',
  inny: 'Inny urlop'
};

const VACATION_KIND_DETAILS_LABEL: Record<VacationKind, string> = {
  wypoczynkowy: 'Wypoczynkowy',
  na_zadanie: 'Na żądanie',
  opieka_nad_dzieckiem: 'Opieka nad dzieckiem',
  inny: 'Inny'
};

const MAX_DAYS_FOR_KIND: Record<VacationKind, number> = {
  wypoczynkowy: 30,
  na_zadanie: 30,
  opieka_nad_dzieckiem: 2,
  inny: 30
};

const formatDateInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateInput = (value: string): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map((n) => Number(n));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, 12, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const addBusinessDaysInclusive = (start: Date, totalDays: number): Date => {
  if (totalDays <= 1) return new Date(start.getTime());
  let remaining = totalDays - 1;
  const cursor = new Date(start.getTime());
  while (remaining > 0) {
    cursor.setDate(cursor.getDate() + 1);
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return cursor;
};

const HomePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { showNotification } = useNotification();

  const fullName = React.useMemo(() => {
    if (user?.display_name && user.display_name.trim()) return user.display_name;
    const composed = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return composed || user?.email || '';
  }, [user]);

  const leaveTotalDays = React.useMemo<number>(() => {
    const raw = (user as any)?.leave_days_entitlement;
    const num = typeof raw === 'number' ? raw : Number(raw ?? 26);
    return Number.isFinite(num) ? num : 26;
  }, [user]);
  const usedLeaveDays = React.useMemo<number>(() => {
    const raw = (user as any)?.leave_days_used;
    const num = typeof raw === 'number' ? raw : Number(raw ?? 0);
    return Math.max(0, Number.isFinite(num) ? num : 0);
  }, [user]);
  const leaveRemainingDays = React.useMemo(() => {
    const r = (user as any)?.leave_days_remaining;
    if (r != null && Number.isFinite(Number(r))) return Math.max(0, Number(r));
    return Math.max(0, leaveTotalDays - usedLeaveDays);
  }, [user, leaveTotalDays, usedLeaveDays]);
  const leavePct = React.useMemo(
    () => Math.max(0, Math.min(100, leaveTotalDays > 0 ? Math.round((usedLeaveDays / leaveTotalDays) * 100) : 0)),
    [leaveTotalDays, usedLeaveDays]
  );

  const [requestModalOpen, setRequestModalOpen] = React.useState<boolean>(false);
  const [requestStep, setRequestStep] = React.useState<'type' | 'vacation-kind' | 'vacation-form' | 'advance-form'>('type');
  const [requestType, setRequestType] = React.useState<RequestType | null>(null);
  const [vacationKind, setVacationKind] = React.useState<VacationKind | null>(null);
  const [vacationStart, setVacationStart] = React.useState<string>(() => formatDateInput(new Date()));
  const [vacationDays, setVacationDays] = React.useState<number>(1);
  const [vacationNotes, setVacationNotes] = React.useState<string>('');
  const [isSubmittingRequest, setIsSubmittingRequest] = React.useState<boolean>(false);
  const [awaitingSignature, setAwaitingSignature] = React.useState<boolean>(false);
  const [signatureInfo, setSignatureInfo] = React.useState<{ text: string; severity: 'info' | 'success' } | null>(null);
  const currentStepNumber = React.useMemo(() => {
    if (requestStep === 'type') return 1;
    if (requestStep === 'vacation-kind') return 2;
    return 3;
  }, [requestStep]);
  const totalSteps = requestType === 'advance' ? 2 : 3;

  const resetRequestWizard = React.useCallback(() => {
    setRequestStep('type');
    setRequestType(null);
    setVacationKind(null);
    setVacationStart(formatDateInput(new Date()));
    setVacationDays(1);
    setVacationNotes('');
    setIsSubmittingRequest(false);
    setSignatureInfo(null);
    setAwaitingSignature(false);
  }, []);

  const handleAddRequest = () => {
    resetRequestWizard();
    setRequestModalOpen(true);
    void refreshUser();
  };
  const handleCloseRequestModal = React.useCallback(() => {
    setRequestModalOpen(false);
    resetRequestWizard();
  }, [resetRequestWizard]);
  const handleNextFromType = () => {
    if (requestType === 'vacation') {
      setRequestStep('vacation-kind');
    } else if (requestType === 'advance') {
      setRequestStep('advance-form');
    }
  };
  const handleNextFromVacationKind = () => {
    if (vacationKind) setRequestStep('vacation-form');
  };
  const handleStepBack = () => {
    if (requestStep === 'vacation-kind') setRequestStep('type');
    else if (requestStep === 'vacation-form') setRequestStep('vacation-kind');
    else if (requestStep === 'advance-form') setRequestStep('type');
  };
  const vacationEndDate = React.useMemo(() => {
    const start = parseDateInput(vacationStart);
    if (!start) return '';
    const end = addBusinessDaysInclusive(start, Math.max(1, vacationDays || 1));
    return formatDateInput(end);
  }, [vacationStart, vacationDays]);
  const handleVacationDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.value) return;
    setVacationStart(e.target.value);
  };
  const usesAnnualLeavePool = vacationKind === 'wypoczynkowy' || vacationKind === 'na_zadanie';
  const maxVacationDays = React.useMemo(() => {
    const cap = vacationKind ? MAX_DAYS_FOR_KIND[vacationKind] : 30;
    if (usesAnnualLeavePool) {
      return Math.max(0, Math.min(cap, leaveRemainingDays));
    }
    return cap;
  }, [vacationKind, usesAnnualLeavePool, leaveRemainingDays]);
  const minVacationDaysUi = maxVacationDays <= 0 ? 0 : 1;
  const handleVacationDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value);
    if (Number.isNaN(raw)) return;
    const next = Math.max(minVacationDaysUi, Math.min(maxVacationDays, Math.round(raw)));
    setVacationDays(next);
  };
  const adjustVacationDays = (delta: number) => {
    setVacationDays((prev) => {
      const next = Math.max(minVacationDaysUi, Math.min(maxVacationDays, Number(prev) + delta));
      return next;
    });
  };
  React.useEffect(() => {
    setVacationDays((prev) => {
      if (maxVacationDays <= 0) return 0;
      return Math.min(Math.max(1, prev), maxVacationDays);
    });
  }, [maxVacationDays]);
  const notesRequired = vacationKind === 'inny';
  const canSubmitVacation = Boolean(
    vacationStart &&
      vacationKind &&
      !isSubmittingRequest &&
      (!notesRequired || vacationNotes.trim().length > 0) &&
      (!usesAnnualLeavePool ||
        (leaveRemainingDays > 0 && vacationDays >= 1 && vacationDays <= leaveRemainingDays))
  );
  const submitSignatureRequest = React.useCallback(async () => {
    if (!vacationStart || !vacationKind) return;
    try {
      setIsSubmittingRequest(true);
      const hrNotes = vacationNotes.trim();
      const baseDetails = vacationKind ? `Rodzaj urlopu: ${VACATION_KIND_DETAILS_LABEL[vacationKind]}` : '';
      const details = [baseDetails, hrNotes].filter(Boolean).join('\n');
      await api.post('/requester/leave-requests/request-signature', {
        date_from: vacationStart,
        days: vacationDays,
        vacation_kind: vacationKind,
        ...(hrNotes ? { hr_notes: hrNotes } : {}),
        ...(details ? { details } : {})
      });
      setSignatureInfo({ severity: 'info', text: 'Dokończ w aplikacji mobilnej' });
      setAwaitingSignature(true);
      showNotification('Dokończ w aplikacji mobilnej', 'info');
      // Spróbuj bezpośrednio otworzyć aplikację mobilną przez deep link (fallback do powiadomień push/socketów)
      try {
        const start = parseDateInput(vacationStart);
        const end = start ? addBusinessDaysInclusive(start, Math.max(1, vacationDays || 1)) : null;
        const dateTo = end ? formatDateInput(end) : vacationStart;
        const params = new URLSearchParams();
        params.set('intent', 'vacation-signature');
        params.set('dateFrom', vacationStart);
        params.set('dateTo', dateTo);
        params.set('days', String(vacationDays || 1));
        if (vacationKind) params.set('vacationKind', vacationKind);
        if (hrNotes) params.set('hrNotes', hrNotes);
        if (details) params.set('details', details);
        const deepLinkUrl = `unitresco://vacation/signature?${params.toString()}`;
        // Krótki delay poprawia zachowanie w przeglądarkach mobilnych
        setTimeout(() => {
          try {
            window.location.href = deepLinkUrl;
          } catch {
            // cichy fallback – dalej polegamy na powiadomieniu push i Socket.IO
          }
        }, 100);
      } catch {
        // Deep link jest tylko dodatkiem – w razie błędu polegamy na powiadomieniu push i Socket.IO
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Nie udało się wysłać powiadomienia.';
      showNotification(message, 'error');
    } finally {
      setIsSubmittingRequest(false);
    }
  }, [vacationStart, vacationKind, vacationDays, vacationNotes, showNotification]);

  const handleSubmitVacation = React.useCallback(() => {
    if (!awaitingSignature && canSubmitVacation) submitSignatureRequest();
  }, [awaitingSignature, canSubmitVacation, submitSignatureRequest]);

  const handleResendSignature = React.useCallback(() => {
    submitSignatureRequest();
  }, [submitSignatureRequest]);

  React.useEffect(() => {
    const socket = getSocket();
    const onPending = () => {
      setAwaitingSignature(true);
      setSignatureInfo({ severity: 'info', text: 'Dokończ w aplikacji mobilnej' });
    };
    const onCompleted = () => {
      setAwaitingSignature(false);
      setSignatureInfo({ severity: 'success', text: 'Wniosek złożony' });
      showNotification('Wniosek złożony', 'success');
      void refreshUser();
      // Po zakończeniu podpisu na telefonie zamknij kreator wniosku i zresetuj stan
      setRequestModalOpen(false);
      resetRequestWizard();
    };
    socket.on('requester:signature:pending', onPending);
    socket.on('requester:signature:completed', onCompleted);
    return () => {
      try { socket.off('requester:signature:pending', onPending); } catch {}
      try { socket.off('requester:signature:completed', onCompleted); } catch {}
    };
  }, [showNotification, resetRequestWizard, refreshUser]);

  const renderRequestOption = (params: { selected: boolean; label: string; description: string; onClick: () => void }) => (
    <Paper
      onClick={params.onClick}
      elevation={0}
      sx={{
        border: params.selected ? '2px solid #4040DB' : '1px solid #E5E7EB',
        borderRadius: '8px',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderColor: '#4040DB',
          boxShadow: '0px 4px 12px rgba(64,64,219,0.1)'
        }
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600, color: '#020242' }}>{params.label}</Typography>
        <Typography sx={{ color: '#595966', fontSize: 14, mt: 0.5 }}>{params.description}</Typography>
      </Box>
      <Box sx={{ color: params.selected ? '#4040DB' : '#C7C7D4' }}>
        {params.selected ? <CheckCircle /> : <RadioButtonUnchecked />}
      </Box>
    </Paper>
  );

  const renderVacationForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {usesAnnualLeavePool && (
        <Alert severity={leaveRemainingDays < 1 ? 'warning' : 'info'} sx={{ py: 0.5 }}>
          Urlop wypoczynkowy: pozostało{' '}
          <strong>
            {Number.isInteger(leaveRemainingDays) ? leaveRemainingDays : leaveRemainingDays.toFixed(1)}
          </strong>{' '}
          {leaveRemainingDays === 1 ? 'dzień' : 'dni'} (przysługuje {leaveTotalDays}, wykorzystano {usedLeaveDays}
          — wnioski urlopowe wliczają się po akceptacji HR).
        </Alert>
      )}
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 0.5 }}>Data rozpoczęcia</Typography>
        <TextField
          type="date"
          value={vacationStart}
          onChange={handleVacationDateChange}
          fullWidth
          InputLabelProps={{ shrink: true }}
          // UWAGA: nie blokujemy dat wstecz — czasami ktoś zapomniał złożyć na czas
          // albo wystąpił problem techniczny i wniosek trzeba zarejestrować wstecznie.
          disabled={awaitingSignature}
          helperText={
            vacationStart && parseDateInput(vacationStart) && parseDateInput(vacationStart)! < new Date(new Date().toDateString())
              ? 'Wybrano datę wsteczną — wniosek zostanie zarejestrowany z tym dniem rozpoczęcia.'
              : undefined
          }
        />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 0.5 }}>Ilość dni</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="primary"
            onClick={() => adjustVacationDays(-1)}
            disabled={vacationDays <= minVacationDaysUi || awaitingSignature || maxVacationDays <= 0}
          >
            <Remove />
          </IconButton>
          <TextField
            type="number"
            value={vacationDays}
            onChange={handleVacationDaysChange}
            inputProps={{ min: minVacationDaysUi, max: maxVacationDays > 0 ? maxVacationDays : 0 }}
            sx={{ width: 120 }}
            disabled={awaitingSignature || maxVacationDays <= 0}
          />
          <IconButton
            color="primary"
            onClick={() => adjustVacationDays(1)}
            disabled={vacationDays >= maxVacationDays || awaitingSignature || maxVacationDays <= 0}
          >
            <Add />
          </IconButton>
        </Box>
        {vacationKind === 'opieka_nad_dzieckiem' && (
          <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.5 }}>
            Opieka nad dzieckiem — przysługują maksymalnie 2 dni w roku kalendarzowym.
          </Typography>
        )}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 0.5 }}>Data zakończenia</Typography>
        <TextField value={vacationEndDate || '-'} InputProps={{ readOnly: true }} fullWidth />
        <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.5 }}>
          Data końcowa wyliczana automatycznie z pominięciem weekendów.
        </Typography>
      </Box>
      <Divider />
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 0.5 }}>
          {notesRequired ? 'Uwagi dla HR (wymagane)' : 'Uwagi dla HR (opcjonalnie)'}
        </Typography>
        <TextField
          value={vacationNotes}
          onChange={(e) => setVacationNotes(e.target.value)}
          placeholder={
            notesRequired
              ? 'Opisz rodzaj urlopu (np. okolicznościowy – ślub, bezpłatny, opieka nad chorym dzieckiem).'
              : 'Np. zastępstwo / dodatkowe informacje'
          }
          multiline
          minRows={3}
          fullWidth
          required={notesRequired}
          error={notesRequired && vacationNotes.trim().length === 0}
          helperText={
            notesRequired && vacationNotes.trim().length === 0
              ? 'Wpisz szczegóły wybierając „Inny”, aby HR mógł poprawnie zakwalifikować wniosek.'
              : undefined
          }
          disabled={awaitingSignature}
        />
      </Box>
      {awaitingSignature && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={22} sx={{ mr: 1 }} />
          <Typography sx={{ color: '#374151', fontSize: 14 }}>Oczekiwanie na podpis w aplikacji mobilnej…</Typography>
        </Box>
      )}
    </Box>
  );

  const renderRequestModal = () => (
    <Dialog open={requestModalOpen} onClose={handleCloseRequestModal} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Dodaj wniosek</Typography>
        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
          Krok {currentStepNumber} z {totalSteps}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {requestStep === 'type' && (
          <>
            <Typography sx={{ fontWeight: 500 }}>Wybierz rodzaj wniosku</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {REQUEST_TYPE_OPTIONS.map((opt) =>
                renderRequestOption({
                  selected: requestType === opt.id,
                  label: opt.label,
                  description: opt.description,
                  onClick: () => setRequestType(opt.id)
                })
              )}
            </Box>
          </>
        )}
        {requestStep === 'vacation-kind' && (
          <>
            <Typography sx={{ fontWeight: 500 }}>Rodzaj urlopu</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {VACATION_KIND_OPTIONS.map((opt) =>
                renderRequestOption({
                  selected: vacationKind === opt.id,
                  label: opt.label,
                  description: opt.description,
                  onClick: () => setVacationKind(opt.id)
                })
              )}
            </Box>
          </>
        )}
        {requestStep === 'vacation-form' && (
          <>
            <Typography sx={{ fontWeight: 500 }}>
              {vacationKind ? VACATION_KIND_LABELS[vacationKind] : 'Urlop wypoczynkowy'}
            </Typography>
            {renderVacationForm()}
          </>
        )}
        {requestStep === 'advance-form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>Wniosek o zaliczkę</Typography>
            <Typography sx={{ color: '#6B7280', fontSize: 14 }}>
              Formularz zaliczek jest w przygotowaniu. Daj znać zespołowi finansów, jeśli potrzebujesz wsparcia już teraz.
            </Typography>
          </Box>
        )}
        {signatureInfo && (
          <Alert severity={signatureInfo.severity} sx={{ mt: 1 }}>
            {signatureInfo.text}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleCloseRequestModal} color="inherit">
            Anuluj
          </Button>
          {requestStep !== 'type' && (
            <Button onClick={handleStepBack} color="inherit">
              Wstecz
            </Button>
          )}
        </Box>
        <Box>
          {requestStep === 'type' && (
            <Button variant="contained" onClick={handleNextFromType} disabled={!requestType}>
              Dalej
            </Button>
          )}
          {requestStep === 'vacation-kind' && (
            <Button variant="contained" onClick={handleNextFromVacationKind} disabled={!vacationKind}>
              Dalej
            </Button>
          )}
          {requestStep === 'vacation-form' && (
            awaitingSignature ? (
              <Button variant="outlined" onClick={handleResendSignature} disabled={isSubmittingRequest}>
                {isSubmittingRequest ? <CircularProgress size={20} color="inherit" /> : 'Wyślij ponownie'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmitVacation} disabled={!canSubmitVacation}>
                {isSubmittingRequest ? <CircularProgress size={20} color="inherit" /> : 'Podpisz wniosek'}
              </Button>
            )
          )}
          {requestStep === 'advance-form' && (
            <Button variant="contained" onClick={() => showNotification('Obsługa zaliczek w przygotowaniu', 'info')} color="primary">
              Powiadom mnie
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );

  const ratingPercent = 100;
  const [starLoaded, setStarLoaded] = React.useState(false);
  const [moneyIconLoaded, setMoneyIconLoaded] = React.useState(false);
  const avatarHiRes = getHiResGoogleAvatar(user?.avatar_url, 256) || '/assets/Rectangle9.png';
  // Kalendarz – dane i filtr
  const [calendarEvents, setCalendarEvents] = React.useState<CalendarEvent[]>([]);
  const [calendarFilter, setCalendarFilter] = React.useState<string>('');
  const [tasks, setTasks] = React.useState<Array<{ id: string; title: string; due?: string | null; status?: 'needsAction'|'completed' }>>([]);
  const [tasksFilter, setTasksFilter] = React.useState<string>('');
  const [notes, setNotes] = React.useState<NoteListItem[]>([]);
  const [notesFilter, setNotesFilter] = React.useState<string>('');
  // Lokalna lista zadań – jedna per użytkownika
  const [composerVisible, setComposerVisible] = React.useState<boolean>(false);
  const [newTaskTitle, setNewTaskTitle] = React.useState<string>('');
  const [newTaskDueYmd, setNewTaskDueYmd] = React.useState<string | null>(null);
  const taskDateInputRef = React.useRef<HTMLInputElement | null>(null);
  // Rejestr wniosków – dane z API + filtr
  const [requestFilter, setRequestFilter] = React.useState<string>('');
  const [myRequests, setMyRequests] = React.useState<Array<{
    id: string | number;
    title: string;
    subline?: string | null;
    status: string;
  }>>([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/requester/leave-requests');
        const rows = res?.data?.data || [];
        const pad = (n: number) => String(n).padStart(2, '0');
        const fmtYmd = (d: any) => {
          try {
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return String(d || '');
            return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
          } catch { return String(d || ''); }
        };
        const mapped = rows.map((r: any) => {
          const days = Number(r?.days || 0);
          const from = fmtYmd(r?.date_from);
          const to = fmtYmd(r?.date_to);
          return {
            id: r.id,
            title: `Wniosek urlopowy - ${days || 1} ${days === 1 ? 'dzień' : 'dni'}`,
            subline: from && to ? `od ${from} do ${to}` : null,
            status: r?.status || 'Złożony'
          };
        });
        if (!cancelled) setMyRequests(mapped);
      } catch {
        if (!cancelled) setMyRequests([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Załaduj notatki + odśwież, gdy ktoś zakończy transkrypcję i auto-generacja wyprodukuje nową notatkę
  React.useEffect(() => {
    let cancelled = false;
    const reload = async () => {
      try {
        const list = await listNotes();
        if (!cancelled) setNotes(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setNotes([]);
      }
    };
    void reload();
    const offNote = onSttNoteGenerated(() => { void reload(); });
    return () => { cancelled = true; try { offNote(); } catch {} };
  }, []);
  const myNotes = React.useMemo(() => {
    const email = (user?.email || '').toLowerCase();
    const display = (user?.display_name || '').toLowerCase();
    return (notes || []).filter(n => {
      const arr = (n.participants || []).map(p => (p || '').toLowerCase());
      return (email && arr.some(p => p.includes(email))) || (display && arr.some(p => p.includes(display)));
    });
  }, [notes, user]);
  const [aiDialogOpen, setAiDialogOpen] = React.useState(false);
  const [aiDialogNoteId, setAiDialogNoteId] = React.useState<string | null>(null);
  const [aiDialogNoteTitle, setAiDialogNoteTitle] = React.useState<string | undefined>(undefined);
  /** Podgląd notatki w prawym drawerze (wywoływany chipem „Przejdź” w kaflu Notatki). */
  const [notePreviewOpen, setNotePreviewOpen] = React.useState<boolean>(false);
  const [notePreviewId, setNotePreviewId] = React.useState<string | null>(null);
  const [publicUsers, setPublicUsers] = React.useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);
  // Proste MD5 do Gravatara (minimalna implementacja bez zależności)
  function md5(str: string): string {
    // eslint-disable-next-line
    function L(k:any, d:any){return(k<<d)|(k>>>(32-d));}
    // eslint-disable-next-line
    function K(G:any, k:any){var I,d,F,H,x;F=(G&2147483648);H=(k&2147483648);I=(G&1073741824);d=(k&1073741824);x=(G&1073741823)+(k&1073741823);if(I&d){return(x^2147483648^F^H);}if(I|d){if(x&1073741824){return(x^3221225472^F^H);}else{return(x^1073741824^F^H);}}else{return(x^F^H);}}
    // eslint-disable-next-line
    function r(d:any,F:any,k:any){return(d&F)|((~d)&k);}
    // eslint-disable-next-line
    function q(d:any, F:any, k:any){return(d&k)|(F&(~k));}
    // eslint-disable-next-line
    function p(d:any,F:any,k:any){return(d^F^k);}
    // eslint-disable-next-line
    function n(d:any,F:any,k:any){return(F^(d|(~k)));} // prettier-ignore
    // eslint-disable-next-line
    function u(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(r(F,aa,Z),k),I));return K(L(G,H),F);}
    // eslint-disable-next-line
    function f(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(q(F,aa,Z),k),I));return K(L(G,H),F);}
    // eslint-disable-next-line
    function D(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(p(F,aa,Z),k),I));return K(L(G,H),F);}
    // eslint-disable-next-line
    function t(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(n(F,aa,Z),k),I));return K(L(G,H),F);}
    // eslint-disable-next-line
    function e(G:any){var Z;var F=G.length;var x=F+8;var k=(x-(x%64))/64;var I=(k+1)*16;var aa=Array(I-1);var d=0;var H=0;while(H<F){Z=(H-(H%4))/4;d=(H%4)*8;aa[Z]=(aa[Z]|(G.charCodeAt(H)<<d));H++;}Z=(H-(H%4))/4;d=(H%4)*8;aa[Z]=(aa[Z]|(128<<d));aa[I-2]=(F<<3);aa[I-1]=(F>>>29);return aa;}
    // eslint-disable-next-line
    function B(x:any){var k="",F="",G,d;for(d=0;d<=3;d++){G=(x>>>(d*8))&255;F="0"+G.toString(16);k=k+F.substr(F.length-2,2);}return k;}
    // eslint-disable-next-line
    function J(k:any){k=k.replace(/\r\n/g,"\n");var d="";for(var F=0;F<k.length;F++){var x=k.charCodeAt(F);if(x<128){d+=String.fromCharCode(x);}else{if((x>127)&&(x<2048)){d+=String.fromCharCode((x>>6)|192);d+=String.fromCharCode((x&63)|128);}else{d+=String.fromCharCode((x>>12)|224);d+=String.fromCharCode(((x>>6)&63)|128);d+=String.fromCharCode((x&63)|128);}}}return d;}
    var C:Array<number>;var m,g,h,y,v,w,E,S,O;var a=7,b=12,c=17,i=22;var l=5,j=9,o=14,_=20;var P=4,A=11,T=16,U=23;var Q=6,R=10,N=15,M=21;str=J(str);C=e(str);g=1732584193;h=4023233417;y=2562383102;v=271733878;for(m=0;m<C.length;m+=16){w=g;E=h;S=y;O=v;g=u(g,h,y,v,C[m+0],a,3614090360);v=u(v,g,h,y,C[m+1],b,3905402710);y=u(y,v,g,h,C[m+2],c,606105819);h=u(h,y,v,g,C[m+3],i,3250441966);g=u(g,h,y,v,C[m+4],a,4118548399);v=u(v,g,h,y,C[m+5],b,1200080426);y=u(y,v,g,h,C[m+6],c,2821735955);h=u(h,y,v,g,C[m+7],i,4249261313);g=u(g,h,y,v,C[m+8],a,1770035416);v=u(v,g,h,y,C[m+9],b,2336552879);y=u(y,v,g,h,C[m+10],c,4294925233);h=u(h,y,v,g,C[m+11],i,2304563134);g=u(g,h,y,v,C[m+12],a,1804603682);v=u(v,g,h,y,C[m+13],b,4254626195);y=u(y,v,g,h,C[m+14],c,2792965006);h=u(h,y,v,g,C[m+15],i,1236535329);g=f(g,h,y,v,C[m+1],l,4129170786);v=f(v,g,h,y,C[m+6],j,3225465664);y=f(y,v,g,h,C[m+11],o,643717713);h=f(h,y,v,g,C[m+0],_,3921069994);g=f(g,h,y,v,C[m+5],l,3593408605);v=f(v,g,h,y,C[m+10],j,38016083);y=f(y,v,g,h,C[m+15],o,3634488961);h=f(h,y,v,g,C[m+4],_,3889429448);g=f(g,h,y,v,C[m+9],l,568446438);v=f(v,g,h,y,C[m+14],j,3275163606);y=f(y,v,g,h,C[m+3],o,4107603335);h=f(h,y,v,g,C[m+8],_,1163531501);g=f(g,h,y,v,C[m+13],l,2850285829);v=f(v,g,h,y,C[m+2],j,4243563512);y=f(y,v,g,h,C[m+7],o,1735328473);h=f(h,y,v,g,C[m+12],_,2368359562);g=D(g,h,y,v,C[m+5],P,4294588738);v=D(v,g,h,y,C[m+8],A,2272392833);y=D(y,v,g,h,C[m+11],T,1839030562);h=D(h,y,v,g,C[m+14],U,4259657740);g=D(g,h,y,v,C[m+1],P,2763975236);v=D(v,g,h,y,C[m+4],A,1272893353);y=D(y,v,g,h,C[m+7],T,4139469664);h=D(h,y,v,g,C[m+10],U,3200236656);g=D(g,h,y,v,C[m+13],P,681279174);v=D(v,g,h,y,C[m+0],A,3936430074);y=D(y,v,g,h,C[m+3],T,3572445317);h=D(h,y,v,g,C[m+6],U,76029189);g=t(g,h,y,v,C[m+0],Q,3654602809);v=t(v,g,h,y,C[m+7],R,3873151461);y=t(y,v,g,h,C[m+14],N,530742520);h=t(h,y,v,g,C[m+5],M,3299628640);g=t(g,h,y,v,C[m+12],Q,4096336452);v=t(v,g,h,y,C[m+3],R,1126891415);y=t(y,v,g,h,C[m+10],N,2878612391);h=t(h,y,v,g,C[m+1],M,4237533241);g=K(g,w);h=K(h,E);y=K(y,S);v=K(v,O);}return (B(g)+B(h)+B(y)+B(v)).toLowerCase();
  }
  const gravatarUrl = (email: string, size = 64) => `https://www.gravatar.com/avatar/${md5(email.trim().toLowerCase())}?d=identicon&s=${size}`;
  const [meetingParticipants, setMeetingParticipants] = React.useState<Record<string, MeetingParticipantDto[]>>({});
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listUsersPublic();
        if (!cancelled) setPublicUsers(Array.isArray(list) ? list : []);
      } catch { if (!cancelled) setPublicUsers([]); }
    })();
    return () => { cancelled = true; };
  }, []);
  // Załaduj uczestników do notatek na kafelku (dla poprawnych avatarów)
  React.useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set((notes || []).map(n => n.meetingGoogleId).filter(Boolean))) as string[];
    if (!ids.length) return;
    (async () => {
      const updates: Record<string, MeetingParticipantDto[]> = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const list = await getParticipants(id);
          updates[id] = list;
        } catch {}
      }));
      if (!cancelled) setMeetingParticipants(prev => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [notes]);
  // Saldo – kwota w PLN
  const balancePln = React.useMemo<number>(() => {
    const raw = (user as any)?.balance;
    const num = typeof raw === 'number' ? raw : Number(raw || 0);
    return Number.isFinite(num) ? num : 0;
  }, [user]);
  const balancePlnFormatted = React.useMemo(
    () => new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(balancePln),
    [balancePln]
  );

  const toIsoFromYmd = (ymd: string | null): string | null => {
    if (!ymd) return null;
    try {
      const [y, m, d] = ymd.split('-').map((v) => parseInt(v, 10));
      if (!y || !m || !d) return null;
      const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // środek dnia, żeby uniknąć stref
      return dt.toISOString();
    } catch {
      return null;
    }
  };

  const reloadTasks = React.useCallback(async () => {
    try {
      const t = await listMyTasks({ showCompleted: false });
      setTasks(t as any);
    } catch {
      setTasks([]);
    }
  }, []);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [eventsRes, tasksRes] = await Promise.allSettled([
          calendarService.getUpcomingEvents(30, 10),
          listMyTasks({ showCompleted: false })
        ]);
        if (cancelled) return;
        if (eventsRes.status === 'fulfilled') setCalendarEvents(eventsRes.value || []);
        else setCalendarEvents([]);
        if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value as any);
        else await reloadTasks();
      } catch {
        if (!cancelled) {
          setCalendarEvents([]);
          setTasks([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, reloadTasks]);

  return (
    <>
      <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek i przycisk Dodaj – zgodnie z układem stron projektów i ofert */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          {`Witaj ${fullName}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            onClick={handleAddRequest}
            data-action="add-request"
            data-hotkey-add="true"
            sx={{ 
              width: 116,
              minWidth: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj wniosek
          </Button>
        </Box>
      </Box>

      {/* Główny kontener – na wzór strony projektu: zdjęcie/awatar + opis i dwa kafelki po prawej */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
        {/* Lewy blok: avatar + opis (układ jak w ProjectDetails) */}
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #EDEDED',
            borderRadius: '4px',
            overflow: 'hidden',
            minHeight: 208,
            height: 208,
            flex: 1,
            bgcolor: '#fff'
          }}
        >
          {/* Avatar/zdjęcie użytkownika */}
          <Box sx={{ position: 'relative', width: 208, height: '100%', flexShrink: 0, backgroundColor: '#D9D9D9' }}>
            <Box
              component="img"
              src={avatarHiRes}
              srcSet={`${getHiResGoogleAvatar(user?.avatar_url, 256) || '/assets/Rectangle9.png'} 1x, ${getHiResGoogleAvatar(user?.avatar_url, 512) || '/assets/Rectangle9.png'} 2x`}
              alt="Avatar użytkownika"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              onError={(e: any) => {
                const tried = e?.currentTarget?.getAttribute('data-tried') || '';
                if (!tried) {
                  e.currentTarget.setAttribute('data-tried', '1');
                  e.currentTarget.src = '/assets/Rectangle9.png';
                }
              }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <IconButton
              component="a"
              href="https://myaccount.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Zmień zdjęcie"
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                p: 0.75,
                m: 0,
                width: 'auto',
                height: 'auto',
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '999px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(2px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
              <Box
                component="img"
                src="/assets/edit_image.png"
                alt="Edytuj"
                onError={(e: any) => {
                  const tried = e?.currentTarget?.getAttribute('data-tried') || '';
                  if (!tried) {
                    e.currentTarget.setAttribute('data-tried', '1');
                    e.currentTarget.src = '/Users/konradtrzeszczkowski/Downloads/edit_image.png';
                  }
                }}
                sx={{ display: 'block', width: 'auto', height: 'auto' }}
              />
            </IconButton>
          </Box>

          {/* Prawy panel treści – opis użytkownika */}
          <Box sx={{ flex: 1, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: '24px', pt: '8px', pb: 0 }}>
              <Box sx={{ display: 'grid', rowGap: 0 }}>
                {[
                  { value: fullName || '-' },
                  { value: user?.position || '-' },
                  { value: user?.telefon || '-' },
                  { value: user?.email || '-' },
                  { value: `Badania lekarskie: ${(user as any)?.next_medical_check_date || '-'}` },
                  { value: `Szkolenie BHP: ${(user as any)?.next_bhp_training_date || '-'}` },
                  { value: 'Moje dokumenty', href: '/mydocs' }
                ].map((row: { value: string; href?: string }, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 28,
                      backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                      px: 0
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        component={row.href ? 'a' : 'span'}
                        href={row.href || undefined}
                        target={row.href && /^https?:\/\//.test(row.href) ? '_blank' : undefined}
                        rel={row.href && /^https?:\/\//.test(row.href) ? 'noopener noreferrer' : undefined}
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '15px',
                          lineHeight: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          color: row.href ? '#2020AF' : '#020242',
                          textDecoration: row.href ? 'underline' : 'none',
                          cursor: row.href ? 'pointer' : 'default',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'left'
                        }}
                        title={String(row.value)}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Prawa karta 1: Saldo (kwota w PLN) */}
        <Box
          sx={{
            width: 214.5,
            minWidth: 214,
            bgcolor: '#fff',
            border: '1px solid #EDEDED',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Saldo</Typography>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px', pb: '24px' }}>
            <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Ikona worka z pieniędzmi – z zasobów public/assets/icons/money_bag.svg; fallback do AttachMoney */}
              {!moneyIconLoaded && <AttachMoney sx={{ width: 60, height: 60, color: '#595966' }} />}
              <Box
                component="img"
                src="/assets/icons/money_bag.svg"
                alt="Saldo"
                onLoad={() => setMoneyIconLoaded(true)}
                onError={() => setMoneyIconLoaded(false)}
                sx={{ width: 60, height: 60, display: moneyIconLoaded ? 'block' : 'none' }}
              />
            </Box>
            <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242', whiteSpace: 'nowrap' }}>
              {balancePlnFormatted} zł
            </Typography>
          </Box>
          <Box sx={{ position: 'absolute', left: '24px', bottom: '24px' }}>
            <Typography
              component="a"
              href="/expenses"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#2020AF',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Rejestr wydatków
            </Typography>
          </Box>
        </Box>

        {/* Prawa karta 2: Wykorzystany urlop (kółko + procent + n/26 dni) */}
        <Box
          sx={{
            width: 214.5,
            minWidth: 214,
            bgcolor: '#fff',
            border: '1px solid #EDEDED',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Wykorzystany urlop</Typography>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px', pb: '24px' }}>
            <Box sx={{ width: 80, height: 80, display: 'grid', placeItems: 'center' }}>
              {(() => {
                const size = 80;
                const stroke = 15;
                const center = size / 2;
                const radius = center - stroke / 2;
                const circumference = 2 * Math.PI * radius;
                const pct = leavePct;
                const dashoffset = circumference * (1 - pct / 100);
                return (
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={center} cy={center} r={radius} stroke="#E6E6EC" strokeWidth={stroke} fill="none" />
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      stroke="#4040DB"
                      strokeWidth={stroke}
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${circumference} ${circumference}`}
                      strokeDashoffset={dashoffset}
                      transform={`rotate(-90 ${center} ${center})`}
                    />
                  </svg>
                );
              })()}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242' }}>
                {leavePct}%
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: '16px', color: '#020242' }}>
                {`${usedLeaveDays}/${leaveTotalDays} dni`}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Prawa karta 3: Ocena (gwiazdka + procent, bez kółka) */}
        <Box
          sx={{
            width: 214.5,
            minWidth: 214,
            bgcolor: '#fff',
            border: '1px solid #EDEDED',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Ocena</Typography>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px', pb: '24px' }}>
            <Box sx={{ position: 'relative', width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!starLoaded && <StarRate sx={{ position: 'absolute', width: 60, height: 60, color: '#595966' }} />}
              <Box
                component="img"
                src="/assets/icons/star_rate.svg"
                alt="Ocena"
                onLoad={() => setStarLoaded(true)}
                onError={() => setStarLoaded(false)}
                sx={{ width: 60, height: 60, display: starLoaded ? 'block' : 'none' }}
              />
            </Box>
            <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242' }}>
              {ratingPercent}%
            </Typography>
          </Box>
        </Box>

      </Box>

      {/* Wiersz: Moje wnioski + Notatki + Zadania + Kalendarz */}
      <Box sx={{ mt: '12px', display: 'flex', justifyContent: 'flex-start', gap: 1.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* Kolumna: Moje wnioski */}
        <Box sx={{ width: { xs: '100%', md: 'calc((100% - 36px)/4)' }, display: 'flex', flexDirection: 'column', gap: 2, alignSelf: 'stretch', height: { xs: 'auto', md: 670 }, minHeight: 0 }}>
          {/* Moje wnioski – rejestr z searchbarem i statusami */}
          <Box sx={{ alignSelf: 'stretch', bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32'
                }}
              >
                Moje wnioski
              </Typography>
            </Box>
            {/* Searchbar */}
            <Box sx={{ mx: '24px', mt: '16px', mb: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  width: '100%',
                  '&:hover': { borderColor: '#B9B9B9' }
                }}
              >
                <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                <InputBase
                  placeholder="Szukaj wniosków"
                  value={requestFilter}
                  onChange={(e)=>setRequestFilter(e.target.value)}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'szukaj wniosków' }}
                />
              </Paper>
            </Box>
            {/* Lista wniosków */}
            <Box sx={{ px: '24px', pt: 2, pb: '16px', display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {myRequests
                .filter(r => {
                  const q = (requestFilter || '').trim().toLowerCase();
                  if (!q) return true;
                  return r.title.toLowerCase().includes(q) || r.status.toLowerCase().includes(q);
                })
                .map((r) => {
                  const norm = (r.status || '')
                    .toString()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                  const statusStyle =
                    norm.includes('zgoda') || norm.includes('zaakceptowa') || norm.includes('approved')
                      ? { bg: '#E6F5E6', fg: '#020242' } // zgoda
                      : norm.includes('odrzucon') || norm.includes('rejected')
                      ? { bg: '#FEE3E3', fg: '#020242' } // odrzucony
                      : { bg: '#E6F0FF', fg: '#020242' }; // złożony / oczekuje
                  return (
                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 34 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, color: '#2D2D32', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.title}
                        </Typography>
                        {r.subline ? (
                          <Typography sx={{ fontSize: 12, color: '#6B7280', mt: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.subline}
                          </Typography>
                        ) : null}
                      </Box>
                      <Chip
                        label={r.status}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.fg,
                          borderRadius: '4px',
                          minHeight: 21,
                          height: 21,
                          '& .MuiChip-label': {
                            px: 1,
                            py: 0,
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '11px',
                            fontWeight: 400,
                            lineHeight: '13px',
                            color: statusStyle.fg
                          }
                        }}
                      />
                    </Box>
                  );
                })}
              {myRequests.filter(r => {
                const q = (requestFilter || '').trim().toLowerCase();
                if (!q) return true;
                return r.title.toLowerCase().includes(q) || r.status.toLowerCase().includes(q);
              }).length === 0 && (
                <Typography sx={{ px: 0, py: 1, color: '#595966', fontSize: 14 }}>Brak wniosków do wyświetlenia.</Typography>
              )}
            </Box>
          </Box>
        </Box>
        {/* Notatki */}
        <Box sx={{ width: { xs: '100%', md: 'calc((100% - 36px)/4)' }, display: 'flex', alignSelf: 'stretch' }}>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, boxSizing: 'border-box', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', height: { xs: 'auto', md: 670 }, minHeight: { xs: 'auto', md: 670 } }}>
            <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32'
                }}
              >
                Notatki
              </Typography>
            </Box>
            {/* Pasek wyszukiwania */}
            <Box sx={{ mx: '24px', mt: '16px', mb: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  width: '100%',
                  '&:hover': { borderColor: '#B9B9B9' }
                }}
              >
                <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                <InputBase
                  placeholder="Szukaj w notatkach"
                  value={notesFilter}
                  onChange={(e)=>setNotesFilter(e.target.value)}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'szukaj w notatkach' }}
                />
              </Paper>
            </Box>
            {/* Lista notatek */}
            <Box sx={{ px: '24px', pt: 2, pb: '16px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
              {(myNotes || [])
                .filter(n => {
                  const q = (notesFilter || '').trim().toLowerCase();
                  if (!q) return true;
                  const inParticipants = (n.participants || []).join(', ').toLowerCase().includes(q);
                  return (n.title || '').toLowerCase().includes(q) || inParticipants;
                })
                .map((n) => {
                  const d = n.dateIso ? new Date(n.dateIso) : null;
                  const formatTime = (dt: Date | null) => {
                    if (!dt || isNaN(dt.getTime())) return n.dateIso || '';
                    const pad = (x: number) => String(x).padStart(2, '0');
                    return `z ${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}, ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                  };
                  const when = formatTime(d);
                  const go = () => {
                    setNotePreviewId(n.id);
                    setNotePreviewOpen(true);
                  };
                  const firstChar = (label: string) => (String(label || '').trim() || '?').charAt(0).toLowerCase();
                  const normalize = (s: string) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g,' ').trim();
                  const findAvatar = (label: string): string | undefined => {
                    const txt = String(label || '').trim();
                    const emailMatch = txt.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0]?.toLowerCase();
                    if (emailMatch) {
                      const u = publicUsers.find(u => (u.email || '').toLowerCase() === emailMatch);
                      if (u?.avatar_url) return u.avatar_url;
                      // fallback: jeśli to ja, użyj mojego avatara z auth
                      if (user?.email && user.email.toLowerCase() === emailMatch) return getHiResGoogleAvatar(user.avatar_url, 64) || undefined;
                      // brak zdjęcia – zwróć undefined, pokażemy inicjał
                      return undefined;
                    }
                    const normTxt = normalize(txt);
                    const u2 = publicUsers.find(u => normalize(u.display_name || '') === normTxt || normalize(u.display_name || '').includes(normTxt) || normTxt.includes(normalize(u.display_name || '')));
                    if (u2?.avatar_url) return u2.avatar_url;
                    // dopasowanie do aktualnego użytkownika po nazwie
                    const myNorm = normalize(user?.display_name || '');
                    if (myNorm === normTxt || myNorm.includes(normTxt) || normTxt.includes(myNorm)) {
                      return getHiResGoogleAvatar(user?.avatar_url || null, 64) || undefined;
                    }
                    return undefined;
                  };
                  return (
                    <Box
                      key={n.id}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.2,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#F5F5FF' },
                        cursor: 'default'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: '2px' }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: '#4040DB',
                            WebkitMaskImage: `url(/assets/icons/article_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg)`,
                            maskImage: `url(/assets/icons/article_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg)`,
                            WebkitMaskRepeat: 'no-repeat',
                            maskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            maskPosition: 'center',
                            WebkitMaskSize: 'contain',
                            maskSize: 'contain'
                          }}
                          aria-hidden
                        />
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, color: '#2D2D32' }}>{n.title || 'Notatka'}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5, width: '100%', alignItems: 'flex-start', transform: 'translateX(-33.6px)' }}>
                          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 11 }, justifyContent: 'flex-start' }}>
                            {(() => {
                              let enriched = meetingParticipants[n.meetingGoogleId || ''] || [];
                              if (enriched.length > 0) {
                                const myEmail = (user?.email || '').toLowerCase();
                                const myNameNorm = normalize(user?.display_name || '');
                                const hasMe = enriched.some(u => (u.email && u.email.toLowerCase() === myEmail) || (normalize(u.display_name || '') === myNameNorm));
                                if (!hasMe && (myEmail || myNameNorm)) {
                                  enriched = [{ email: user?.email || null, display_name: user?.display_name || null, avatar_url: getHiResGoogleAvatar(user?.avatar_url || null, 64) || user?.avatar_url || null }, ...enriched];
                                }
                                return enriched.slice(0, 8).map((u, idx) => {
                                  const isMe = (u.email && u.email.toLowerCase() === myEmail) || (normalize(u.display_name || '') === myNameNorm);
                                  const src = isMe ? (getHiResGoogleAvatar(user?.avatar_url || null, 64) || undefined) : (u.avatar_url || undefined);
                                  return (
                                    <Tooltip key={`${u.email || u.display_name || idx}`} title={u.display_name || u.email || ''}>
                                      <Avatar src={src} alt={u.display_name || u.email || ''}>
                                        {firstChar(u.display_name || u.email || '')}
                                      </Avatar>
                                    </Tooltip>
                                  );
                                });
                              }
                              return (n.participants || []).slice(0, 8).map((p, idx) => (
                                <Tooltip key={`${p}-${idx}`} title={p}>
                                  {(() => {
                                    const src = findAvatar(p);
                                    return (
                                      <Avatar src={src} alt={p}>
                                        {firstChar(p)}
                                      </Avatar>
                                    );
                                  })()}
                                </Tooltip>
                              ));
                            })()}
                          </AvatarGroup>
                          <Chip
                            label={when}
                            size="small"
                            sx={{
                              bgcolor: '#FFF5CC',
                              color: '#020242',
                              borderRadius: '4px',
                              minHeight: 21,
                              height: 21,
                              '& .MuiChip-label': {
                                px: 1,
                                py: 0,
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '11px',
                                fontWeight: 400,
                                lineHeight: '13px',
                                color: '#020242'
                              }
                            }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, position: 'absolute', right: '12px', bottom: '12px' }}>
                            <Chip
                              label="Zadania AI"
                              size="small"
                              clickable
                              onClick={(e)=>{ e.stopPropagation(); setAiDialogNoteId(n.id); setAiDialogNoteTitle(n.title); setAiDialogOpen(true); }}
                              sx={{
                                height: 18,
                                borderRadius: '4px',
                                fontWeight: 700,
                                fontSize: 10,
                                bgcolor: '#FEE3E3',
                                color: '#020242',
                                '& .MuiChip-label': { px: 0.8 }
                              }}
                            />
                            <Chip
                              label="Przejdź"
                              size="small"
                              clickable
                              color="primary"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); go(); }}
                              sx={{
                                height: 18,
                                borderRadius: '4px',
                                fontWeight: 700,
                                fontSize: 10,
                                '& .MuiChip-label': { px: 0.8 },
                                '&:hover': { backgroundColor: 'primary.light', color: '#fff', borderColor: 'primary.light' }
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                  );
                })}
              {(Array.isArray(notes) && notes.length === 0) && (
                <Typography sx={{ px: 0, py: 1, color: '#595966', fontSize: 14 }}>Brak notatek do wyświetlenia.</Typography>
              )}
            </Box>
            <NoteAiTasksDialog
              open={aiDialogOpen}
              onClose={()=>setAiDialogOpen(false)}
              noteId={aiDialogNoteId || ''}
              noteTitle={aiDialogNoteTitle}
            />
            <NotePreviewDrawer
              open={notePreviewOpen}
              noteId={notePreviewId}
              onClose={() => { setNotePreviewOpen(false); setNotePreviewId(null); }}
            />
          </Box>
        </Box>
        {/* Zadania */}
        <Box sx={{ width: { xs: '100%', md: 'calc((100% - 36px)/4)' }, display: 'flex', alignSelf: 'stretch' }}>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, boxSizing: 'border-box', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', height: { xs: 'auto', md: 670 }, minHeight: { xs: 'auto', md: 670 } }}>
            <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32'
                }}
              >
                Zadania
              </Typography>
            </Box>
            {/* Pasek: Dodaj zadanie + szukaj */}
            <Box sx={{ mx: '24px', mt: '16px', mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => { setComposerVisible(true); }}
                sx={{
                  height: 36,
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: '#4040DB',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#1F1FB0' },
                  '&:active': { backgroundColor: '#1A1A98' }
                }}
              >
                Dodaj zadanie
              </Button>
              <Paper
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  flex: 1,
                  '&:hover': { borderColor: '#B9B9B9' }
                }}
              >
                <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                <InputBase
                  placeholder="Szukaj w zadaniach"
                  value={tasksFilter}
                  onChange={(e)=>setTasksFilter(e.target.value)}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'szukaj w zadaniach' }}
                />
              </Paper>
            </Box>
            {/* Kompozytor nowego zadania */}
            {composerVisible && (
              <Box sx={{ mx: '24px', mt: 1.5, p: 1, borderRadius: '6px', backgroundColor: '#F5F5FF' }}>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    placeholder="Nazwa zadania"
                    value={newTaskTitle}
                    onChange={(e)=>setNewTaskTitle(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={6}
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        const title = newTaskTitle.trim();
                        if (!title) return;
                        const dueIso = toIsoFromYmd(newTaskDueYmd);
                        await createMyTask({ title, due: dueIso });
                        setNewTaskTitle('');
                        setNewTaskDueYmd(null);
                        setComposerVisible(false);
                        await reloadTasks();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setComposerVisible(false);
                        setNewTaskTitle('');
                        setNewTaskDueYmd(null);
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '4px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                      '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: newTaskTitle ? '#2D2D32' : '#B9B9B9' },
                      '& .MuiInputBase-inputMultiline': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: newTaskTitle ? '#2D2D32' : '#B9B9B9' },
                      '& .MuiInputBase-input::placeholder': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px', color: '#B9B9B9', opacity: 1 }
                    }}
                  />
                </Stack>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(() => {
                    const today = new Date();
                    const pad = (n: number) => String(n).padStart(2, '0');
                    const todayYmd = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;
                    const tmrw = new Date(); tmrw.setDate(tmrw.getDate()+1);
                    const tmrwYmd = `${tmrw.getFullYear()}-${pad(tmrw.getMonth()+1)}-${pad(tmrw.getDate())}`;
                    const baseChip = {
                      size: 'small' as const,
                      sx: {
                        borderRadius: '4px',
                        minHeight: 21,
                        height: 21,
                        '& .MuiChip-label': {
                          px: 1, py: 0, fontFamily: 'Roboto, sans-serif', fontSize: '11px', fontWeight: 400, lineHeight: '13px'
                        }
                      }
                    };
                    return (
                      <>
                        <Chip
                          label="Dzisiaj"
                          onClick={async ()=>{
                            const title = newTaskTitle.trim();
                            if (title) {
                              const dueIso = toIsoFromYmd(todayYmd);
                              await createMyTask({ title, due: dueIso });
                              setNewTaskTitle('');
                              setNewTaskDueYmd(null);
                              setComposerVisible(false);
                              await reloadTasks();
                            } else {
                              setNewTaskDueYmd(todayYmd);
                            }
                          }}
                          {...baseChip}
                          sx={{ ...baseChip.sx, bgcolor: newTaskDueYmd===todayYmd ? '#FADFBF' : '#FEEED6', color: '#020242' }}
                        />
                        <Chip
                          label="Jutro"
                          onClick={async ()=>{
                            const title = newTaskTitle.trim();
                            if (title) {
                              const dueIso = toIsoFromYmd(tmrwYmd);
                              await createMyTask({ title, due: dueIso });
                              setNewTaskTitle('');
                              setNewTaskDueYmd(null);
                              setComposerVisible(false);
                              await reloadTasks();
                            } else {
                              setNewTaskDueYmd(tmrwYmd);
                            }
                          }}
                          {...baseChip}
                          sx={{ ...baseChip.sx, bgcolor: newTaskDueYmd===tmrwYmd ? '#DDE8FF' : '#E6F0FF', color: '#020242' }}
                        />
                        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                          <IconButton size="small" onClick={() => taskDateInputRef.current?.showPicker?.()}>
                            <CalendarMonth fontSize="small" />
                          </IconButton>
                          <input
                            ref={taskDateInputRef}
                            type="date"
                            value={newTaskDueYmd || ''}
                            onChange={async (e)=>{
                              const value = e.target.value || null;
                              const title = newTaskTitle.trim();
                              if (title && value) {
                                const dueIso = toIsoFromYmd(value);
                                await createMyTask({ title, due: dueIso });
                                setNewTaskTitle('');
                                setNewTaskDueYmd(null);
                                setComposerVisible(false);
                                await reloadTasks();
                              } else {
                                setNewTaskDueYmd(value);
                              }
                            }}
                            onKeyDown={async (e)=>{
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = (e.currentTarget as HTMLInputElement).value || null;
                                const title = newTaskTitle.trim();
                                if (title && value) {
                                  const dueIso = toIsoFromYmd(value);
                                  await createMyTask({ title, due: dueIso });
                                  setNewTaskTitle('');
                                  setNewTaskDueYmd(null);
                                  setComposerVisible(false);
                                  await reloadTasks();
                                }
                              }
                            }}
                            style={{ position: 'absolute', inset: 0, opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                          />
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            )}
            {/* Lista zadań (scrollowalna) */}
            <Box sx={{ px: '24px', pt: 2, pb: '16px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
              {(() => {
                const filtered = (Array.isArray(tasks) ? tasks : []).filter(t => {
                  const q = (tasksFilter || '').trim().toLowerCase();
                  if (!q) return true;
                  return (t.title || '').toLowerCase().includes(q);
                });
                return filtered.slice(0, 10).map((t, idx) => {
                  const formatDue = (iso?: string | null) => {
                    if (!iso) return undefined;
                    try {
                      const d = new Date(iso);
                      if (isNaN(d.getTime())) return undefined;
                      const pad = (x: number) => String(x).padStart(2, '0');
                      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
                    } catch { return undefined; }
                  };
                  const due = formatDue(t.due || null);
                  return (
                    <Box
                      key={t.id || idx}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.2,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#F5F5FF' }
                      }}
                    >
                      <Box sx={{ mt: '2px' }}>
                        <Checkbox
                          size="small"
                          onChange={async () => {
                            try {
                              if (!t.id) return;
                              await completeMyTask(t.id);
                              await reloadTasks();
                            } catch {}
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, color: '#2D2D32' }}>{t.title || 'Zadanie'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, justifyContent: 'space-between', width: '100%' }}>
                          {due ? (
                            <Chip
                              label={due}
                              size="small"
                              sx={{
                                bgcolor: '#FFF5CC',
                                color: '#020242',
                                borderRadius: '4px',
                                minHeight: 21,
                                height: 21,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0,
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '11px',
                                  fontWeight: 400,
                                  lineHeight: '13px',
                                  color: '#020242'
                                }
                              }}
                            />
                          ) : <span />}
                        </Box>
                      </Box>
                    </Box>
                  );
                });
              })()}
              {(Array.isArray(tasks) && tasks.length === 0) && (
                <Typography sx={{ px: 0, py: 1, color: '#595966', fontSize: 14 }}>Brak zadań do wyświetlenia.</Typography>
              )}
            </Box>
          </Box>
        </Box>
        {/* Kalendarz */}
        <Box sx={{ width: { xs: '100%', md: 'calc((100% - 36px)/4)' }, display: 'flex', alignSelf: 'stretch' }}>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, boxSizing: 'border-box', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', height: { xs: 'auto', md: 670 }, minHeight: { xs: 'auto', md: 670 } }}>
            <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32'
                }}
              >
                Kalendarz
              </Typography>
            </Box>
            {/* Pasek wyszukiwania */}
            <Box sx={{ mx: '24px', mt: '16px', mb: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  width: '100%',
                  '&:hover': { borderColor: '#B9B9B9' }
                }}
              >
                <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                <InputBase
                  placeholder="Szukaj wydarzenia"
                  value={calendarFilter}
                  onChange={(e)=>setCalendarFilter(e.target.value)}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'szukaj wydarzenia' }}
                />
              </Paper>
            </Box>
            {/* Lista wydarzeń (scrollowalna) */}
            <Box sx={{ px: '24px', pt: 2, pb: '16px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
              {(calendarEvents || [])
                .filter(ev => calendarFilter.trim() ? (ev.summary || '').toLowerCase().includes(calendarFilter.trim().toLowerCase()) : true)
                .map((ev, idx) => {
                  const startIso = ev.start || '';
                  const d = startIso ? new Date(startIso) : null;
                  const formatTime = (dt: Date | null) => {
                    if (!dt || isNaN(dt.getTime())) return startIso || '';
                    const pad = (x: number) => String(x).padStart(2, '0');
                    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}, ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                  };
                  const when = formatTime(d);
                  const go = () => {
                    try {
                      if (ev.htmlLink) { window.open(ev.htmlLink, '_blank', 'noopener'); return; }
                      window.open('https://calendar.google.com/', '_blank', 'noopener');
                    } catch {}
                  };
                  return (
                    <Box
                      key={ev.id || idx}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.2,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#F5F5FF' },
                        cursor: 'pointer'
                      }}
                      onClick={go}
                    >
                      <Box sx={{ mt: '2px' }}>
                        <CalendarMonth sx={{ width: 24, height: 24, color: '#2020AF' }} />
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: 14, color: '#2D2D32' }}>{ev.summary || 'Wydarzenie'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, justifyContent: 'space-between', width: '100%' }}>
                          <Chip
                            label={when}
                            size="small"
                            sx={{
                              bgcolor: '#FFF5CC',
                              color: '#020242',
                              borderRadius: '4px',
                              minHeight: 21,
                              height: 21,
                              '& .MuiChip-label': {
                                px: 1,
                                py: 0,
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '11px',
                                fontWeight: 400,
                                lineHeight: '13px',
                                color: '#020242'
                              }
                            }}
                          />
                          <Chip
                            label="Przejdź"
                            size="small"
                            clickable
                            color="primary"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); go(); }}
                            sx={{
                              height: 18,
                              borderRadius: '4px',
                              fontWeight: 700,
                              fontSize: 10,
                              ml: 'auto',
                              '& .MuiChip-label': { px: 0.8 },
                              '&:hover': { backgroundColor: 'primary.light', color: '#fff', borderColor: 'primary.light' }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
              })}
              {(!calendarEvents || calendarEvents.length === 0) && (
                <Typography sx={{ px: 0, py: 1, color: '#595966', fontSize: 14 }}>Brak nadchodzących wydarzeń.</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Link do edycji zdjęcia profilu Google (zgodnie z wymaganiem) */}
      {/* https://myaccount.google.com/ */}

      {/* Tu w przyszłości pojawią się widgety strony głównej */}
      </Box>
      {renderRequestModal()}
    </>
  );
};

export default HomePage;
